/**
 * When the landing page's seasonal strip shows, and what it says. Evaluated
 * against the visitor's local date, so it turns itself on and off each year
 * with no deploy:
 *
 *   Sep–Nov  standard   — plan ahead, book by November
 *   Dec      last call  — more urgent, for late January planners
 *   Jan–Aug  hidden
 *
 * To change the season or wording, edit `PHASES` below.
 */

/** Rendered as: `${lead} ` + <a>{link}</a> + `${tail}`. */
export interface StripMessage {
  lead: string;
  link: string;
  tail: string;
}

type Phase = { months: number[]; message: StripMessage };

// Months are JS Date months: 0 = January … 11 = December.
const PHASES: Record<"standard" | "lastCall", Phase> = {
  standard: {
    months: [8, 9, 10],
    message: {
      lead: "Planning a Year in Review recap?",
      link: "Get your free sample now",
      tail: ", and book by November for January campaign.",
    },
  },
  lastCall: {
    months: [11],
    message: {
      lead: "Last call for January Year in Review campaigns.",
      link: "Get your free sample this week",
      tail: ".",
    },
  },
};

export type StripPreview = keyof typeof PHASES | "off";

/** The strip message for `date`, or null when the strip should be hidden. */
export function getYearEndStripMessage(date: Date): StripMessage | null {
  const month = date.getMonth();
  for (const phase of Object.values(PHASES)) {
    if (phase.months.includes(month)) return phase.message;
  }
  return null;
}

/**
 * Dev-only override so each phase can be checked locally before its season:
 * `?strip=standard`, `?strip=lastCall` or `?strip=off`. Ignored in production.
 */
export function resolveYearEndStrip(
  date: Date,
  search: string,
  isDev: boolean,
): StripMessage | null {
  if (isDev) {
    const preview = new URLSearchParams(search).get("strip");
    if (preview === "off") return null;
    if (preview && Object.prototype.hasOwnProperty.call(PHASES, preview)) {
      return PHASES[preview as keyof typeof PHASES].message;
    }
  }
  return getYearEndStripMessage(date);
}
